// Local mirror of the Vault record we hand around UI state. The
// `@cef-ai/vault-sdk` exposes a `Vault` handle (the live HTTP-bound object
// returned by `sdk.vault.current()`/`ensure()`). Handles are convenient for
// SDK calls, but we keep the plain-data record for storage, display, and
// React state because it serializes cleanly and never goes stale.

import type { Vault as VaultHandle } from '@cef-ai/vault-sdk';

/** Whether vault-api can still maintain the vault's storage credential.
 *  `disconnected` means the delegate grant was revoked/expired and refresh
 *  failed — the owner must re-authorize. */
export type VaultStatus = 'active' | 'disconnected';

export interface VaultRecord {
  vaultId: string;
  bucketId: string;
  gatewayEndpoint: string;
  walletPubkey: string;
  region?: string;
  status: VaultStatus;
}

export function vaultHandleToRecord(handle: VaultHandle): VaultRecord {
  return {
    vaultId: handle.id,
    bucketId: handle.bucketId,
    gatewayEndpoint: handle.gatewayEndpoint,
    walletPubkey: handle.walletPubkey,
    region: handle.region,
    status: vaultHandleStatus(handle),
  };
}

/** Prefer the fluent `isDisconnected()` method (the SDK's live signal). If a
 *  handle predates it, fall back to a raw `status`/`disconnected` wire field
 *  (older backends/fixtures may serialize either shape). A handle missing
 *  every signal is treated as `active` so the disconnected banner never
 *  fires spuriously. */
function vaultHandleStatus(handle: VaultHandle): VaultStatus {
  const withSignals = handle as VaultHandle & {
    isDisconnected?: () => boolean;
    status?: VaultStatus;
    disconnected?: boolean;
  };
  const disconnected =
    withSignals.isDisconnected?.() ??
    (withSignals.status === 'disconnected' || withSignals.disconnected === true);
  return disconnected ? 'disconnected' : 'active';
}

export type { VaultHandle };
